"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

export default function Home() {
  const router = useRouter();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 通过后端接口查询数据库 user 表做登录校验。
  const handleLogin = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setMessage("");
    setErrorMessage("");

    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          password,
          rememberMe,
        }),
      });

      if (response.ok) {
        router.push("/admin");
        return;
      }

      const payload = (await response.json()) as { message?: string };
      setErrorMessage(payload.message ?? "登录失败，请稍后重试。");
    } catch {
      setErrorMessage("网络异常，请稍后重试。");
    } finally {
      setIsSubmitting(false);
    }
  };

  // 演示用：点击功能入口时给出页面内提示。
  const handleRegister = () => {
    setErrorMessage("");
    setMessage("新用户注册功能开发中。");
  };

  const handleForgotPassword = () => {
    setErrorMessage("");
    setMessage("忘记密码功能开发中。");
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-sky-50 via-indigo-50 to-violet-100 px-4 py-6 sm:py-10">
      <div className="mx-auto w-full max-w-sm rounded-2xl border border-white/70 bg-white/90 p-5 shadow-[0_18px_45px_-15px_rgba(79,70,229,0.35)] backdrop-blur sm:p-7">
        <h1 className="mb-5 text-center text-2xl font-semibold text-gray-900 sm:mb-6">
          用户登录
        </h1>

        {message && (
          <p className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            {message}
          </p>
        )}

        <form className="space-y-4">
          <div>
            <label
              htmlFor="username"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              用户名
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="请输入用户名"
              className="w-full rounded-lg border border-gray-300/90 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              密码
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="请输入密码"
                className="w-full rounded-lg border border-gray-300/90 bg-white px-3 py-2.5 pr-11 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "隐藏密码" : "显示密码"}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
              >
                {showPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="h-5 w-5"
                  >
                    <path d="M3 3l18 18" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                    <path d="M9.4 5.4A10.7 10.7 0 0 1 12 5c5.2 0 9.3 4.2 10 7-.3 1.2-1.3 2.8-2.8 4.1" />
                    <path d="M6.2 6.2C3.8 7.8 2.3 10.1 2 12c.7 2.8 4.8 7 10 7 1.5 0 2.9-.3 4.2-.8" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="h-5 w-5"
                  >
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {errorMessage && (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {errorMessage}
            </p>
          )}

          <div className="flex items-center justify-between text-sm">
            <label className="flex cursor-pointer items-center gap-2 text-gray-700">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              记住我
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-indigo-600 transition hover:text-indigo-700 hover:underline"
            >
              忘记密码？
            </button>
          </div>

          <button
            type="button"
            onClick={handleLogin}
            disabled={isSubmitting}
            className="w-full rounded-lg bg-indigo-600 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-400"
          >
            {isSubmitting ? "登录中..." : "登录"}
          </button>

          <button
            type="button"
            onClick={handleRegister}
            className="w-full rounded-lg border border-indigo-600 py-2.5 text-sm font-medium text-indigo-600 transition hover:bg-indigo-50"
          >
            新用户注册
          </button>
        </form>
      </div>
    </main>
  );
}
